/**
 * Standard care-time field for call-based clinical documentation.
 *
 * Pairs the minutes input with the {@link DialpadCallPicker} so every surface
 * that documents a call (encounter forms, quick-add, intake) presents the same
 * pattern: enter minutes, or select the specific recent call between this
 * clinician and the patient. Selecting a call sets the minutes and links the
 * call for traceability.
 *
 * @agentic connections
 */

import { Stack } from "@mantine/core";
import { CareTimeActivityDurationField } from "./CareTimeActivityDurationField";
import { DialpadCallPicker } from "./DialpadCallPicker";
import type { RecentCall } from "@/hooks/domain/useRecentPatientCalls";

/** Props for `CallDocumentationField`. @agentic connections */
export type CallDocumentationFieldProps = {
  patientId: string | undefined;
  minutes: number | undefined;
  onMinutesChange: (value: number | undefined) => void;
  /** Currently linked call, if any. */
  selectedCommunicationId?: string;
  /** Fired when a call is selected, or `undefined` to clear the link. */
  onSelectCall: (call: RecentCall | undefined) => void;
  /** Practitioner role to scope calls to. Defaults to the current user's role. */
  practitionerRoleId?: string;
  /** Minutes-field label. */
  label?: string;
  /** Minutes-field helper text. */
  helperText?: string;
};

/**
 * Minutes input + recent-call picker, the standard call-documentation control.
 * @agentic connections
 */
export const CallDocumentationField = ({
  patientId,
  minutes,
  onMinutesChange,
  selectedCommunicationId,
  onSelectCall,
  practitionerRoleId,
  label,
  helperText,
}: CallDocumentationFieldProps) => (
  <Stack gap="sm">
    <CareTimeActivityDurationField
      minutes={minutes}
      onMinutesChange={onMinutesChange}
      label={label}
      helperText={helperText}
    />
    <DialpadCallPicker
      patientId={patientId}
      selectedCommunicationId={selectedCommunicationId}
      onSelect={onSelectCall}
      practitionerRoleId={practitionerRoleId}
    />
  </Stack>
);
